import { useMemo, useState, useTransition } from 'react'
import { Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useLivePage } from '../api/hooks'
import { usePagePoolArticles } from '../api/hooks/useArticles'
import { usePublishedSlotArticles, PageSlotRenderer } from '../components/page-renderer/PageSlotRenderer'
import { PageMetricsToolbar } from '../components/page-renderer/PageMetricsToolbar'
import { PageMetricsSunburst } from '../components/page-renderer/PageMetricsSunburst'
import { usePageInlineMetrics } from '../components/page-renderer/usePageInlineMetrics'
import PageLoader from '../components/common/PageLoader'
import type { CanvasSlot } from '../components/editor-shared/canvasTypes'
import type { BlockContent, PageBlock } from '../api/types'
import { useAuthz } from '../auth/useAuthz'

// ─── PageBlock[] → CanvasSlot[] + contentMap ──────────────────────────────────

function pageBlocksToSlots(blocks: PageBlock[]): CanvasSlot[] {
  return blocks
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((b) => ({
      id: b.id,
      type: b.type,
      order: b.order,
      config: b.config,
      columnBlocks: b.columnContent
        ? Object.fromEntries(
            Object.entries(b.columnContent).map(([k, colBlocks]) => [
              k,
              (colBlocks ?? []).map((cb) => ({ id: cb.id, type: cb.type })),
            ]),
          )
        : undefined,
    }))
}

function pageBlocksToContentMap(blocks: PageBlock[]): Record<string, BlockContent> {
  const map: Record<string, BlockContent> = {}
  for (const b of blocks) {
    if (b.content) map[b.id] = b.content
    if (b.columnContent) {
      for (const [colKey, colBlocks] of Object.entries(b.columnContent)) {
        for (const cb of colBlocks ?? []) {
          if (cb.content) map[`${b.id}:${colKey}:${cb.id}`] = cb.content
        }
      }
    }
  }
  return map
}

// ─── FrontPage ────────────────────────────────────────────────────────────────

export default function FrontPage() {
  const navigate = useNavigate()
  const { isWorkflowMember } = useAuthz()
  const [adminMode, setAdminMode] = useState(false)
  const [, startTransition] = useTransition()
  const [hoverMetricsTarget, setHoverMetricsTarget] = useState<{ blockId: string | null; articleId: string | null } | null>(null)
  const [lockedMetricsTarget, setLockedMetricsTarget] = useState<{ blockId: string | null; articleId: string | null } | null>(null)
  const { data: page, isPending: isPagePending, isError } = useLivePage()
  const { articles: poolArticles, isPending: isPoolPending } = usePagePoolArticles(page?.pool)

  const slots = page ? pageBlocksToSlots(page.blocks) : []
  const contentMap = page ? pageBlocksToContentMap(page.blocks) : {}
  const { slotArticleMap, isPending: isSlotPending } = usePublishedSlotArticles(slots, contentMap, page?.pool ? poolArticles : undefined)
  const displayedArticleIds = useMemo(
    () => Array.from(new Set(Object.values(slotArticleMap).flat().map(article => article.id))),
    [slotArticleMap],
  )
  const { metricsByArticleId: pageMetricsByArticleId, isLoading: pageMetricsLoading } = usePageInlineMetrics(adminMode, page?.id, displayedArticleIds)
  const totalPageViewsFromPage = useMemo(
    () => Object.values(pageMetricsByArticleId).reduce((sum, metrics) => sum + metrics.viewsFromPage, 0),
    [pageMetricsByArticleId],
  )
  const activeMetricsTarget = lockedMetricsTarget ?? hoverMetricsTarget

  if (isPagePending || isPoolPending || isSlotPending) {
    return <PageLoader />
  }

  if (isError || !page) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-fg-secondary">
        No front page is currently live.
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl px-6 pb-12 pt-6">
      <div className="mb-4">
        <PageMetricsToolbar
          adminMode={adminMode}
          canToggleAdminMode={isWorkflowMember}
          onToggleAdminMode={isWorkflowMember ? () => startTransition(() => setAdminMode(current => !current)) : undefined}
        />
      </div>
      {adminMode && pageMetricsLoading && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-fg-secondary">
          <Loader2 size={14} className="animate-spin" aria-hidden="true" />
          Loading inline page metrics...
        </div>
      )}
      {adminMode && totalPageViewsFromPage > 0 && (
        <div className="mb-8">
          <PageMetricsSunburst
            slots={slots}
            slotArticleMap={slotArticleMap}
            pageMetricsByArticleId={pageMetricsByArticleId}
            totalPageViewsFromPage={totalPageViewsFromPage}
            lockedTarget={lockedMetricsTarget}
            onHoverChange={target => {
              if (!lockedMetricsTarget) setHoverMetricsTarget(target)
            }}
            onLockToggle={target => {
              setLockedMetricsTarget(current => {
                if (current && current.blockId === target.blockId && current.articleId === target.articleId) {
                  return null
                }
                return target
              })
            }}
          />
        </div>
      )}
      <div className="space-y-10">
        {Object.values(slotArticleMap).every((a) => a.length === 0) ? (
          <div className="flex h-48 items-center justify-center text-sm text-fg-secondary">
            No content available.
          </div>
        ) : (
          slots.map((slot) => (
            <PageSlotRenderer
              key={slot.id}
              slot={slot}
              articles={slotArticleMap[slot.id] ?? []}
              slotArticleMap={slotArticleMap}
              showPageMetrics={adminMode && !pageMetricsLoading}
              pageMetricsByArticleId={pageMetricsByArticleId}
              totalPageViewsFromPage={totalPageViewsFromPage}
              activeBlockId={activeMetricsTarget?.blockId ?? null}
              activeArticleId={activeMetricsTarget?.articleId ?? null}
              onArticleClick={(article) => navigate(`/wire/articles/${article.id}`, { state: { navMethod: 'page', pageId: page?.id } })}
            />
          ))
        )}
      </div>
    </div>
  )
}
